/**
 * Room messages <-> MeshCore channel text.
 *
 * Over LoRa a room is plain channel text, readable in the stock MeshCore app:
 *   text         -> the text itself
 *   quick action -> "⚠️ NEED HELP @ 38.640,-121.330"   (3 decimals ~ 100 m)
 *   voice note   -> "🎤 <transcript>"                   (audio never rides LoRa)
 * Everything else (system lines, state sync, vehicle sightings) stays off air.
 *
 * Vehicle sightings are excluded on purpose: plate data on a radio channel is
 * a surveillance record the moment anyone captures a node (see the plan doc).
 *
 * Frames CrowdTalkie sends carry the author and a 4-character tag of the
 * room message id:   "Ana› meet at the fountain ·k3f9"
 * The tag is how the same message is recognised wherever it shows up: a
 * gateway that hears it on air stands down instead of sending a second copy,
 * and a phone that has the original from the internet does not add the radio
 * copy. Frames from the stock MeshCore app have neither and still work.
 */

import { QUICK_ACTIONS, type QuickActionKey } from '../types'
import type { MessageEntry } from '../sync/crdt'

const enc = new TextEncoder()

/** POI types that may go on air. */
const RADIO_POI_TYPES: ReadonlySet<QuickActionKey> = new Set(
  (Object.keys(QUICK_ACTIONS) as QuickActionKey[]).filter((k) => k !== 'vehicle_sighting')
)

const AUTHOR_MARK = '› '
const TAG_MARK = ' ·'
const MAX_AUTHOR_BYTES = 16
const TAG_PATTERN = / ·([0-9a-z]{4})$/

function fnv1a(text: string): number {
  let h = 0x811c9dc5
  for (const b of enc.encode(text)) {
    h ^= b
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h
}

/** 4 base-36 chars naming a room message on air (same on every phone). */
export function messageTag(messageId: string): string {
  return (fnv1a(messageId) % 36 ** 4).toString(36).padStart(4, '0')
}

/** True for entries that came in over the radio: they never go back on air. */
export function isRadioOrigin(entry: MessageEntry): boolean {
  return entry.senderId.startsWith('lora:') || entry.radioTag !== undefined
}

/** Cut to at most `maxBytes` of UTF-8 without splitting a character; mark the cut. */
export function truncateUtf8(text: string, maxBytes: number): string {
  if (enc.encode(text).length <= maxBytes) return text
  const ellipsis = '…'
  const room = maxBytes - enc.encode(ellipsis).length
  let out = ''
  let used = 0
  for (const ch of text) {
    const n = enc.encode(ch).length
    if (used + n > room) break
    out += ch
    used += n
  }
  return out + ellipsis
}

function poiLine(poiType: QuickActionKey, lat: number, lng: number): string {
  const action = QUICK_ACTIONS[poiType]
  return `${action.emoji} ${action.label.toUpperCase()} @ ${lat.toFixed(3)},${lng.toFixed(3)}`
}

/**
 * The channel text for a room message ("Author› line ·tag"), or null if it
 * must not go on air. Never exceeds `budgetBytes`; the line is what gets cut.
 */
export function formatForRadio(entry: MessageEntry, budgetBytes: number): string | null {
  if (isRadioOrigin(entry)) return null
  let line: string | null = null
  if (entry.type === 'text' && entry.content?.trim()) {
    line = entry.content.trim()
  } else if (
    entry.type === 'poi' &&
    entry.poiType &&
    RADIO_POI_TYPES.has(entry.poiType) &&
    typeof entry.locationLat === 'number' &&
    typeof entry.locationLng === 'number'
  ) {
    line = poiLine(entry.poiType, entry.locationLat, entry.locationLng)
  } else if (entry.type === 'voice' && entry.transcript?.trim()) {
    line = `🎤 ${entry.transcript.trim()}`
  }
  if (line === null) return null
  const author = truncateUtf8(entry.senderName.replace(/[\n›]/g, ' ').trim() || 'someone', MAX_AUTHOR_BYTES)
  const head = `${author}${AUTHOR_MARK}`
  const tail = `${TAG_MARK}${messageTag(entry.id)}`
  const room = budgetBytes - enc.encode(head).length - enc.encode(tail).length
  if (room < 8) return null
  return `${head}${truncateUtf8(line, room)}${tail}`
}

export interface RadioText {
  /** The sending node's name, from the firmware's "<name>: " prefix. */
  senderName: string
  /** The room author, when a CrowdTalkie phone sent it ("Ana› ..."). */
  author?: string
  /** The message tag, when a CrowdTalkie phone sent it ("... ·k3f9"). */
  tag?: string
  body: string
  poi?: { poiType: QuickActionKey; lat: number; lng: number }
}

const POI_PATTERN = /^(\S+) (.+?) @ (-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)$/

/** Split "<node>: [Author› ]<body>[ ·tag]" and recognise quick-action lines. */
export function parseFromRadio(text: string): RadioText {
  const cut = text.indexOf(': ')
  const senderName = cut > 0 ? text.slice(0, cut) : 'radio'
  let body = cut > 0 ? text.slice(cut + 2) : text
  const out: RadioText = { senderName, body }
  const tag = TAG_PATTERN.exec(body)
  if (tag) {
    out.tag = tag[1]
    body = body.slice(0, tag.index)
    const a = body.indexOf(AUTHOR_MARK)
    if (a > 0 && a <= MAX_AUTHOR_BYTES + 4) {
      out.author = body.slice(0, a)
      body = body.slice(a + AUTHOR_MARK.length)
    }
  }
  out.body = body
  const m = POI_PATTERN.exec(body)
  if (m) {
    const [, emoji, label, lat, lng] = m
    for (const key of RADIO_POI_TYPES) {
      const action = QUICK_ACTIONS[key]
      if (action.emoji === emoji && action.label.toUpperCase() === label) {
        out.poi = { poiType: key, lat: Number(lat), lng: Number(lng) }
      }
    }
  }
  return out
}

/**
 * Same id on every phone that hears the same frame, so two radios in one room
 * collapse into one message in the shared CRDT. FNV-1a over the identifying
 * fields; not a security property, only a dedupe key.
 */
export function radioMessageId(roomId: string, text: string, senderTimestamp: number): string {
  const h = fnv1a(`${roomId}\u0000${senderTimestamp}\u0000${text}`)
  return `lora-${senderTimestamp.toString(36)}-${h.toString(16).padStart(8, '0')}`
}

/** Room id of the radio copy of a tagged message (same on every phone). */
export function taggedCopyId(tag: string): string {
  return `lora-t-${tag}`
}

/** A room message entry for text heard on the radio. */
export function entryFromRadio(roomId: string, text: string, senderTimestamp: number): MessageEntry {
  const parsed = parseFromRadio(text)
  const who = parsed.author ?? parsed.senderName
  const base = {
    id: parsed.tag ? taggedCopyId(parsed.tag) : radioMessageId(roomId, text, senderTimestamp),
    senderId: `lora:${who}`,
    senderName: `${who} 📡`,
    senderRole: 'participant' as const,
    timestamp: senderTimestamp * 1000,
    ...(parsed.tag ? { radioTag: parsed.tag } : {}),
  }
  if (parsed.poi) {
    return {
      ...base,
      type: 'poi',
      poiType: parsed.poi.poiType,
      locationLat: parsed.poi.lat,
      locationLng: parsed.poi.lng,
      content: parsed.body,
    }
  }
  return { ...base, type: 'text', content: parsed.body }
}
